/**
 * StaffTable — the one table shape for a staff roster or queue (#3103;
 * guideline §3.3).
 *
 * Every staff list since the 2026-09-17 staff-lists wave is a real `<table>`:
 * `w-full border-collapse`, an `aria-label` that names the list, the header
 * row hidden below `lg`, the name column `w-full max-w-0` and every other
 * column `w-px whitespace-nowrap`, folding under the name on a phone. Fifteen
 * files declared their own copy of that narrow-column class string, and by
 * October they had drifted three ways (`py-2` against `py-2.5`, `align-top`
 * against `align-middle`, one copy without the fold). This file is the one
 * copy.
 *
 * - `NARROW` — a column that shrinks to its content and folds under the name
 *   below `lg`. Callers add type and colour with `cn(NARROW, …)`.
 * - `NARROW_ALWAYS` — the same column without the fold, for the cell a phone
 *   still needs on the line (a reference, an assignment control).
 * - `NARROW_STACKED` — the `lg:`-only shape for a row that is a wrapping flex
 *   line on a phone (Families roster, Users): below `lg` each cell sets its
 *   own phone padding, so this only shapes the desktop table.
 * - `STAFF_THEAD` — the header row group, hidden below `lg`.
 * - `StaffTable` — the overflow container and the table. `aria-label` is
 *   required: a staff table without a name is a defect, not a default.
 */
import type { ReactNode, TableHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

/** A narrow column at every width: shrinks to its content, the name column absorbs the rest. */
export const NARROW_ALWAYS = 'w-px whitespace-nowrap px-3 py-2.5 align-middle';

/** A narrow column that folds under the name below `lg`, where the header row hides too. */
export const NARROW = `hidden lg:table-cell ${NARROW_ALWAYS}`;

/** A narrow column on a row whose phone layout stacks (a flex line below `lg`); shapes `lg` only. */
export const NARROW_STACKED = 'lg:w-px lg:whitespace-nowrap lg:px-3 lg:py-2.5 lg:align-middle';

/** The header row group: hidden below `lg`, where the narrow columns fold under the name. */
export const STAFF_THEAD = 'hidden lg:table-header-group';

export interface StaffTableProps extends Omit<TableHTMLAttributes<HTMLTableElement>, 'aria-label'> {
  /** The list's name ("Awards", "Reimbursement queue") — required. */
  'aria-label': string;
  /** Classes for the overflow container around the table. */
  containerClassName?: string;
  children: ReactNode;
}

export function StaffTable({
  'aria-label': ariaLabel,
  className,
  containerClassName,
  children,
  ...rest
}: StaffTableProps) {
  return (
    <div className={cn('overflow-x-auto', containerClassName)}>
      <table {...rest} aria-label={ariaLabel} className={cn('w-full border-collapse', className)}>
        {children}
      </table>
    </div>
  );
}
